import { useMemo, useState } from "react"
import { MenuProps, Space } from "antd"
import { ButtonOptions } from "components/ui/buttons/ButtonOptions/ButtonOptions"
import { Dropdown } from "components/ui/dropdown/Dropdown"
import { ChangeRoleModal } from "./layout/ChangeRoleModal/ChangeRoleModal"
import { ChangeStatusModal } from "./layout/ChangeStatusModal/ChangeStatusModal"
import { DeactivateIcon } from "components/ui/icons/userActions/DeactivateIcon"
import { ActivateIcon } from "components/ui/icons/userActions/ActivateIcon"
import { MakeAnAdminIcon } from "components/ui/icons/userActions/MakeAnAdminIcon"
import { MakeAnExternalUserIcon } from "components/ui/icons/userActions/MakeAaExternalUserIcon"
import { MakeAnInternalUserIcon } from "components/ui/icons/userActions/MakeAnInternalUserIcon"
import { DeleteIcon } from "components/ui/icons/userActions/DeleteIcon"
import { DeleteUserModal } from "./layout/DeleteUserModal/DeleteUserModal"
import type { IUserAction } from "types/table.type"
import style from "./TeamTable.module.scss"
import { useCurrentUserStore } from "store/user.store"

const { actionBlock, deleteUserText, deleteUserBlock, deleteUserItem } = style

type Props = { availableRoles: string[]; availableStatus: boolean; record: IUserAction }

export const TeamTableDropdown = ({ record, availableRoles, availableStatus }: Props) => {
	const [isRoleModalOpen, setRoleModalOpen] = useState(false)
	const [isStateModalOPen, setStateModalOpen] = useState(false)
	const [userRoleAction, setUserRoleAction] = useState<{ id: number; newRole: string }>({
		id: 0,
		newRole: "",
	})
	const [userStatusAction, setUserStatusAction] = useState<{ id: number; newStatus: string }>({
		id: 0,
		newStatus: "",
	})

	const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false)
	const [userDeleteAction, setUserDeleteAction] = useState<{
		id: number
		name: string
	}>({
		id: 0,
		name: "",
	})

	const { currentUser } = useCurrentUserStore()

	const renderIcon = (role: string) => {
		switch (role) {
			case "Admin":
				return <MakeAnAdminIcon />
			case "External User":
				return <MakeAnExternalUserIcon />
			case "Internal User":
				return <MakeAnInternalUserIcon />
		}
	}

	// Dynamic dropdown items for roles and status
	const manageItems = useMemo(() => {
		const items: MenuProps["items"] = []

		const id = record.id
		const name = record.name

		// Show item roles
		items.push(
			...availableRoles.map((role) => ({
				key: `role-${role}`,
				label: (
					<div className={actionBlock}>
						{renderIcon(role)}
						<span>{`Make an ${role}`}</span>
					</div>
				),
				onClick: () => {
					setUserRoleAction({
						newRole: role,
						id,
					})
					setRoleModalOpen(true)
				},
			})),
		)

		// Show item status
		items.push({
			key: `isActive-${availableStatus}`,
			label: availableStatus ? (
				<div className={actionBlock}>
					<DeactivateIcon /> <span>Deactivate</span>
				</div>
			) : (
				<div className={actionBlock}>
					<ActivateIcon /> <span>Activate</span>
				</div>
			),
			onClick: () => {
				setUserStatusAction({
					newStatus: availableStatus ? "inactive" : "active",
					id,
				})
				setStateModalOpen(true)
			},
		})

		// Show deletion only for Super Admin
		if (currentUser.role === "Super Admin") {
			// Show deletion item
			items.push(
				{
					type: "divider",
				},
				{
					key: `delete-${id}`,
					danger: true,
					disabled: availableStatus,
					label: (
						<div className={deleteUserBlock}>
							<div className={deleteUserItem}>
								<DeleteIcon inactive={availableStatus} />
								<span>Delete</span>
							</div>
							{availableStatus ? (
								<span className={deleteUserText}>To delete a user, you must deactivate them first.</span>
							) : null}
						</div>
					),
					onClick: () => {
						setUserDeleteAction({
							id,
							name,
						})
						setIsDeleteUserModalOpen(true)
					},
				},
			)
		}

		// Return items
		return items
	}, [availableRoles, availableStatus, record.id, record.name, currentUser.role])

	return (
		<>
			<Dropdown items={manageItems}>
				<a onClick={(e) => e.preventDefault()}>
					<Space>
						<ButtonOptions />
					</Space>
				</a>
			</Dropdown>
			<ChangeRoleModal
				isModalOpen={isRoleModalOpen}
				userAction={userRoleAction}
				setModalOpen={setRoleModalOpen}
				record={record}
			/>
			<ChangeStatusModal
				isModalOpen={isStateModalOPen}
				setModalOpen={setStateModalOpen}
				userAction={userStatusAction}
				record={record}
			/>
			<DeleteUserModal
				isModalOpen={isDeleteUserModalOpen}
				setDeleteUserModalClose={() => setIsDeleteUserModalOpen(false)}
				userData={userDeleteAction}
			/>
		</>
	)
}
